import {useEffect, useRef, useState} from "react";
import {
  SettingParam,
  Action,
  ActionTypes,
  SettingParamTypes,
  WorkflowNodeSetting,
} from "@/types/node";
import {Button} from "../ui/Button";
import {
  ArrowLeftRight,
  Calendar,
  ChevronLeft,
  Plus,
  Zap,
  ChevronRight,
  Loader2,
  X,
  TriangleAlert,
  Info,
  ArrowUp,
  SearchIcon,
} from "lucide-react";
import {Icon} from "@/components/ui/icon";
import {
  DropdownDialogItem,
  DropdownDialogItemContent,
  DropdownDialogItemTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination";

import {getNodeActions} from "../../api";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {DialogHeader} from "../ui/dialog";
import {DialogTitle} from "@radix-ui/react-dialog";
import {useToast} from "@/hooks/useToast";
import {useReactFlow} from "@xyflow/react";
import {getSettingValueByKey} from "@/utils/index";
import {Alert, AlertDescription} from "@/components/ui/Alert";
import {Input} from "@/components/ui/input";
import {httpModeColors} from "@/constants/apiMethods";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {motion} from "framer-motion";

const ActionsTableHeader = (actionType: string) => {
  switch (actionType) {
    case ActionTypes.REAL_TIME_BOOKING:
      return (
        <>
          <TableHead></TableHead>
          <TableHead className="w-full">Event</TableHead>
          <TableHead className="whitespace-nowrap">Booking Date</TableHead>
        </>
      );
    case ActionTypes.LIVE_TRANSFER:
      return (
        <>
          <TableHead>Number to</TableHead>
          <TableHead>When should the transfer take place?</TableHead>
        </>
      );
    case ActionTypes.CUSTOM_ACTION:
      return (
        <>
          <TableHead></TableHead>
          <TableHead>Action</TableHead>
          <TableHead>Mode</TableHead>
        </>
      );
    default:
      return null;
  }
};

const ActionsTableBody = (actionType: string, action: Action) => {
  switch (actionType) {
    case ActionTypes.REAL_TIME_BOOKING:
      return (
        <>
          <TableCell className="font-medium"></TableCell>
          <TableCell className="font-medium">
            {action.parameters_hard_coded.title ||
              action.parameters_hard_coded.event_type_id}
          </TableCell>
          <TableCell>{action.parameters_hard_coded.first_appt_date}</TableCell>
        </>
      );
    case ActionTypes.LIVE_TRANSFER:
      return (
        <>
          <TableCell>{action.parameters_hard_coded.to_phone_number}</TableCell>
          <TableCell>
            <span className="line-clamp-2 overflow-hidden text-ellipsis break-words">
              {action.description}
            </span>
          </TableCell>
        </>
      );
    case ActionTypes.CUSTOM_ACTION:
      return (
        <>
          <TableCell className="pr-0">
            <Icon className="bg-muted text-label border-0">
              {action.icon ? (
                <img
                  className="aspect-square h-full w-full"
                  src={action.icon}
                />
              ) : (
                <Zap className="w-4 h-4" />
              )}
            </Icon>
          </TableCell>
          <TableCell className="font-medium">
            <div className="leading-none">
              <h3 className="text-sm text-primary font-medium">
                {action.name}
              </h3>
              <span
                className="text-sm text-secondary line-clamp-2 overflow-hidden text-ellipsis break-words"
                title={action.parameters_hard_coded.url}
              >
                {action.parameters_hard_coded.url}
              </span>
            </div>
          </TableCell>
          <TableCell>
            <div className="flex items-center">
              <div
                className={`${httpModeColors[action.parameters_hard_coded.http_mode]} text-xs font-bold px-2 py-1 rounded-md`}
              >
                {action.parameters_hard_coded.http_mode}
              </div>
            </div>
          </TableCell>
        </>
      );
    default:
      return null;
  }
};

export const ActionTable = ({
  param,
  handleChange,
  closeDialog,
  selectedActions,
  actionType,
  selectedNodeId,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: Action[]) => void;
  closeDialog?: () => void;
  selectedActions: Action[];
  actionType: string;
  selectedNodeId: string;
}) => {
  const {toast} = useToast();
  const {getNode} = useReactFlow();
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginatedActions, setPaginatedActions] = useState<Action[]>([]);
  const [filteredActions, setFilteredActions] = useState<Action[]>([]);
  const itemsPerPage = 6;
  const modelId = FlowBuilderSettings.modelId;

  const currentNode = getNode(selectedNodeId);
  const nodeActions = [
    ...((getSettingValueByKey(
      currentNode.data.settings as WorkflowNodeSetting[],
      "actions",
    ) as Action[]) || []),
    ...((getSettingValueByKey(
      currentNode.data.settings as WorkflowNodeSetting[],
      "global_actions",
    ) as Action[]) || []),
  ];

  const nodeRealTimeBookings = nodeActions.filter(
    action => action.action_type === ActionTypes.REAL_TIME_BOOKING,
  );

  const searchKeys = [
    "name",
    "parameters_hard_coded.url",
    "parameters_hard_coded.title",
    "parameters_hard_coded.event_type_id",
    "parameters_hard_coded.to_phone_number",
  ];

  const getNestedValue = (obj, path) =>
    path.split(".").reduce((acc, key) => acc && acc[key], obj);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleActionClick = (action: Action) => {
    if (selectedActions.some(a => a.action_id === action.action_id)) {
      toast({
        icon: (
          <Icon
            size="tiny"
            variant="rounded"
            className="bg-red-500 border-0 shadow-toast-icon-red mr-3"
          >
            <TriangleAlert className="text-slate-800 size-2.5 font-bold" />
          </Icon>
        ),
        title: "Action already selected",
      });
      return;
    }
    const newSelectedActions = [...selectedActions, action];
    handleChange(param, newSelectedActions);
    closeDialog && closeDialog();
  };

  useEffect(() => {
    const fetchActions = async (actionType: string) => {
      if (
        nodeRealTimeBookings.length > 0 &&
        actionType === ActionTypes.REAL_TIME_BOOKING
      ) {
        setActions(nodeRealTimeBookings);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getNodeActions({
          model_id: modelId,
          action_type: actionType,
        });

        setActions(data.response.actions);
        setFilteredActions(data.response.actions);
        setPaginatedActions(
          data.response.actions.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage,
          ),
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchActions(actionType);
  }, [actionType]);

  useEffect(() => {
    if (searchTerm.length === 0) {
      setFilteredActions(actions);
      setPaginatedActions(
        actions.slice(
          (currentPage - 1) * itemsPerPage,
          currentPage * itemsPerPage,
        ),
      );
      return;
    }

    const filteredActions = actions.filter(action =>
      searchKeys.some(key => {
        const value = getNestedValue(action, key);
        return value && value.toLowerCase().includes(searchTerm.toLowerCase());
      }),
    );

    setFilteredActions(filteredActions);
    setPaginatedActions(
      filteredActions.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      ),
    );
  }, [searchTerm, currentPage]);

  return (
    <div className="my-4 px-4">
      <div className="flex items-center justify-between mb-4">
        <Input
          containerClassName="w-full"
          startContent={<SearchIcon className="w-4 h-4 stroke-[#AAB5C5]" />}
          placeholder="Search by name or ID"
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
        />
      </div>
      <div className="flex flex-col">
        {nodeRealTimeBookings.length > 0 &&
          actionType === ActionTypes.REAL_TIME_BOOKING && (
            <Alert variant="warning" className="mb-4">
              <Info className="h-5 w-5" />
              <AlertDescription>
                We currently support only one Real-Time Booking Action per Node.
                To choose another Real-Time Booking Action, please remove the
                current one from the node.
              </AlertDescription>
            </Alert>
          )}
        <Table className="w-full rounded-lg border">
          <TableHeader>
            <TableRow>{ActionsTableHeader(actionType)}</TableRow>
          </TableHeader>
          <TableBody className="border-b">
            {loading ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <div className="flex justify-center items-center">
                    <Loader2 className="size-10 animate-spin" />
                  </div>
                </TableCell>
              </TableRow>
            ) : paginatedActions.length == 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <div className="flex justify-center items-center">
                    <span className="text-sm text-secondary">
                      No actions found
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedActions.map((action, index) => (
                <TableRow
                  key={action.action_id + index}
                  onClick={() => {
                    handleActionClick(action);
                  }}
                  className="cursor-pointer hover:bg-muted"
                >
                  {ActionsTableBody(actionType, action)}
                </TableRow>
              ))
            )}
          </TableBody>
          {actions.length > itemsPerPage && (
            <TableFooter className="border-b border-t-0">
              <TableRow>
                <TableCell colSpan={3}>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            if (currentPage > 1)
                              handlePageChange(currentPage - 1);
                          }}
                          isActive={currentPage > 1}
                        >
                          <ChevronLeft className="h-4 w-4 text-secondary" />
                        </PaginationPrevious>
                      </PaginationItem>

                      {(() => {
                        const totalPages = Math.ceil(
                          filteredActions.length / itemsPerPage,
                        );
                        const pages = [];
                        const maxVisiblePages = 1;

                        let shouldShowLeftEllipsis = false;
                        let shouldShowRightEllipsis = false;

                        for (let i = 1; i <= totalPages; i++) {
                          if (
                            i === 1 || // Always show first page
                            i === totalPages || // Always show last page
                            (i >= currentPage - maxVisiblePages &&
                              i <= currentPage + maxVisiblePages) // Show pages in range
                          ) {
                            pages.push(
                              <PaginationItem key={i}>
                                <PaginationLink
                                  href="#"
                                  onClick={e => {
                                    e.preventDefault();
                                    handlePageChange(i);
                                  }}
                                  isActive={currentPage === i}
                                >
                                  {i}
                                </PaginationLink>
                              </PaginationItem>,
                            );
                          } else {
                            // left ellipsis
                            if (
                              i < currentPage - maxVisiblePages &&
                              !shouldShowLeftEllipsis
                            ) {
                              pages.push(
                                <PaginationItem key="left-ellipsis">
                                  <span className="px-2">...</span>
                                </PaginationItem>,
                              );
                              shouldShowLeftEllipsis = true;
                            }

                            // right ellipsis
                            if (
                              i > currentPage + maxVisiblePages &&
                              !shouldShowRightEllipsis
                            ) {
                              pages.push(
                                <PaginationItem key="right-ellipsis">
                                  <span className="px-2">...</span>
                                </PaginationItem>,
                              );
                              shouldShowRightEllipsis = true;
                            }
                          }
                        }
                        return pages;
                      })()}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            if (
                              currentPage <
                              Math.ceil(filteredActions.length / itemsPerPage)
                            )
                              handlePageChange(currentPage + 1);
                          }}
                          isActive={
                            currentPage <
                            Math.ceil(filteredActions.length / itemsPerPage)
                          }
                        >
                          <ChevronRight className="h-4 w-4 text-secondary" />
                        </PaginationNext>
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
};

export const SettingAction = ({
  param,
  handleChange,
  selectedNodeId,
  disabled = false,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: Action[]) => void;
  selectedNodeId: string;
  disabled: boolean;
}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectedActions = (param.value || []) as Action[];

  const closeDialog = () => setIsDialogOpen(false);
  return (
    <div className="mb-4 flex flex-nowrap gap-2 flex-col">
      <DropdownMenu
        modal={false}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      >
        <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
          {param.label}
        </label>
        {selectedActions.length > 0 && (
          <div
            className={`flex items-center gap-2 flex-wrap w-full ${
              disabled
                ? "opacity-50 pointer-events-none cursor-not-allowed"
                : ""
            }`}
          >
            {selectedActions.map(action => {
              let actionDisplayName = "";
              let actionIcon = <Zap className="w-4.5 h-4.5" />;

              switch (action.action_type) {
                case ActionTypes.REAL_TIME_BOOKING:
                  actionDisplayName =
                    action.parameters_hard_coded.title ||
                    action.parameters_hard_coded.event_type_id;
                  actionIcon = <Calendar className="w-4.5 h-4.5" />;
                  break;
                case ActionTypes.LIVE_TRANSFER:
                  actionDisplayName =
                    action.parameters_hard_coded.to_phone_number;
                  actionIcon = <ArrowLeftRight className="w-4.5 h-4.5" />;
                  break;
                case ActionTypes.CUSTOM_ACTION:
                  actionDisplayName = action.name;
                  actionIcon = <Zap className="w-4.5 h-4.5" />;
                  break;
              }
              return (
                <div
                  key={action.action_id}
                  className="text-xs text-primary font-medium p-2 border rounded-md flex items-center gap-2"
                >
                  <Icon className="text-primary-accent border-1 bg-primary-accent w-4.5 h-4.5">
                    {actionIcon}
                  </Icon>
                  {actionDisplayName}
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="ml-auto"
                    onClick={() => {
                      const newSelectedActions = selectedActions.filter(
                        a => a.action_id !== action.action_id,
                      );
                      handleChange(param, newSelectedActions);
                    }}
                  >
                    <X className="text-slate-400" />
                  </Button>
                </div>
              );
            })}
            <DropdownMenuTrigger ref={triggerRef} className="flex-grow flex">
              <Button variant="outline" className="px-3 h-9.5 w-9.5" asDiv>
                <Plus className="text-label w-4 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
          </div>
        )}
        {selectedActions.length === 0 && (
          <DropdownMenuTrigger asChild ref={triggerRef} className="w-full">
            <Button
              variant="outline"
              className={`w-full px-3`}
              disabled={disabled}
              asDiv
            >
              <Zap className="text-violet w-4 h-4" />
              Add Action
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          triggerRef={triggerRef}
          className={`w-[400px] min-w-[400px] max-w-[400px]`}
          side="bottom"
          align="end"
          sideOffset={8}
        >
          <DropdownDialogItem>
            <DropdownDialogItemTrigger>
              <div className="flex items-start text-left gap-3">
                <Icon className="bg-muted text-label border-0">
                  <Calendar className="w-4.5 h-4.5" />
                </Icon>
                <div className="leading-none">
                  <h3 className="text-sm text-primary font-medium">
                    Real-Time Booking
                  </h3>
                  <span className="text-sm text-secondary">
                    Schedule live appointments during the call.
                  </span>
                </div>
              </div>
            </DropdownDialogItemTrigger>
            <DropdownDialogItemContent>
              <DialogHeader>
                <DialogTitle className="text-primary font-medium text-lg">
                  Add Real-Time Booking
                </DialogTitle>
              </DialogHeader>
              <ActionTable
                param={param}
                handleChange={handleChange}
                closeDialog={closeDialog}
                selectedActions={selectedActions}
                actionType={ActionTypes.REAL_TIME_BOOKING}
                selectedNodeId={selectedNodeId}
              />
            </DropdownDialogItemContent>
          </DropdownDialogItem>
          <DropdownDialogItem>
            <DropdownDialogItemTrigger>
              <div className="flex items-start text-left gap-3">
                <Icon className="bg-muted text-label border-0">
                  <ArrowLeftRight className="w-4.5 h-4.5" />
                </Icon>
                <div className="leading-none">
                  <h3 className="text-sm text-primary font-medium">
                    Call Transfer
                  </h3>
                  <span className="text-sm text-secondary">
                    Transfer the call live to a human.
                  </span>
                </div>
              </div>
            </DropdownDialogItemTrigger>
            <DropdownDialogItemContent>
              <DialogHeader>
                <DialogTitle className="text-primary font-medium text-lg">
                  Add Call Transfer
                </DialogTitle>
              </DialogHeader>
              <ActionTable
                param={param}
                handleChange={handleChange}
                closeDialog={closeDialog}
                selectedActions={selectedActions}
                actionType={ActionTypes.LIVE_TRANSFER}
                selectedNodeId={selectedNodeId}
              />
            </DropdownDialogItemContent>
          </DropdownDialogItem>
          <DropdownDialogItem>
            <DropdownDialogItemTrigger>
              <div className="flex items-start gap-3">
                <Icon className="bg-muted text-label border-0">
                  <Zap className="w-4.5 h-4.5" />
                </Icon>
                <div className="leading-none">
                  <h3 className="text-sm text-primary font-medium">
                    Custom Actions
                  </h3>
                  <span className="text-sm text-secondary">
                    Allow your assistants to automatically trigger custom API.
                  </span>
                </div>
              </div>
            </DropdownDialogItemTrigger>
            <DropdownDialogItemContent>
              <DialogHeader>
                <DialogTitle className="text-primary font-medium text-lg">
                  Add Custom Actions
                </DialogTitle>
              </DialogHeader>
              <ActionTable
                param={param}
                handleChange={handleChange}
                closeDialog={closeDialog}
                selectedActions={selectedActions}
                actionType={ActionTypes.CUSTOM_ACTION}
                selectedNodeId={selectedNodeId}
              />
            </DropdownDialogItemContent>
          </DropdownDialogItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
